import { useCallback, useEffect, useRef } from "react";

// ─── Call tones ────────────────────────────────────────────────────────────
// Generated with Web Audio rather than played from audio files: nothing to
// host or download, no autoplay-policy issues mid-call, and the cadence can
// be defined precisely. Two patterns:
//   inbound  — the classic two-burst "ring ring ... ring ring" pattern an
//              agent hears when a call arrives
//   outbound — the single longer burst an agent hears while the far end is
//              ringing (ringback), so it's obvious the call is actually
//              progressing rather than dead air
//
// Lifted verbatim from the working SIP webphone. Do not "simplify" the
// timer bookkeeping — see the note in stop() about the inbound cadence.

export type ToneKind = "inbound" | "outbound";

export function useCallTones() {
    const audioCtxRef = useRef<AudioContext | null>(null);
    const toneStopRef = useRef<(() => void) | null>(null);

    const stop = useCallback(() => {
        if (toneStopRef.current) {
            toneStopRef.current();
            toneStopRef.current = null;
        }
    }, []);

    const start = useCallback(
        (kind: ToneKind) => {
            stop(); // never layer two tones on top of each other
            try {
                if (!audioCtxRef.current) {
                    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
                    audioCtxRef.current = new Ctx();
                }
                const ctx = audioCtxRef.current;
                if (!ctx) return;
                // Browsers suspend the context until a user gesture; by the
                // time a call exists the agent has clicked something, so
                // this normally resolves immediately.
                if (ctx.state === "suspended") ctx.resume().catch(() => {});

                let cancelled = false;
                const timers: ReturnType<typeof setTimeout>[] = [];

                // One burst of tone: two sine waves (a real telephone ring
                // is two frequencies together, which is why a single tone
                // sounds more like a beep than a ring).
                const burst = (durationSec: number) => {
                    if (cancelled || !ctx) return;
                    const gain = ctx.createGain();
                    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02); // soft attack, avoids a click
                    gain.gain.setValueAtTime(0.15, ctx.currentTime + durationSec - 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationSec);
                    gain.connect(ctx.destination);
                    for (const freq of [440, 480]) {
                        const osc = ctx.createOscillator();
                        osc.type = "sine";
                        osc.frequency.value = freq;
                        osc.connect(gain);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + durationSec);
                    }
                };

                const cycle = () => {
                    if (cancelled) return;
                    if (kind === "inbound") {
                        // ring-ring, pause — repeats every 3s
                        burst(0.4);
                        timers.push(
                            setTimeout(() => {
                                if (!cancelled) burst(0.4);
                            }, 600),
                        );
                        timers.push(setTimeout(cycle, 3000));
                    } else {
                        // single long burst, longer pause — repeats every 5s
                        burst(1.0);
                        timers.push(setTimeout(cycle, 5000));
                    }
                };
                cycle();

                toneStopRef.current = () => {
                    cancelled = true;
                    // Clear ALL pending timers — the inbound cadence sets
                    // two per cycle, so tracking only the most recent would
                    // let a stray second burst fire ~600ms after the agent
                    // had already answered.
                    for (const t of timers) clearTimeout(t);
                    timers.length = 0;
                };
            } catch (e) {
                console.error("[TONE] failed to start", e);
            }
        },
        [stop],
    );

    // Teardown lives here rather than in the UA effect so the tone lifecycle
    // is owned entirely by this hook.
    useEffect(() => {
        return () => {
            if (toneStopRef.current) toneStopRef.current(); // stop any ring/ringback still playing
            toneStopRef.current = null;
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => {});
                audioCtxRef.current = null;
            }
        };
    }, []);

    return { start, stop };
}
